import { useCallback, useEffect, useRef, useState, type ChangeEvent, type MouseEvent } from 'react'
import { useIntl } from 'react-intl'
import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'

import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import TextButton from 'LuxKit/Button/TextButton'
import IconButton from 'LuxKit/Button/IconButton'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import AISparkleIcon from 'components/Search/SearchForm/AISparkleIcon'
import LineEllipsisVIcon from 'LuxKit/Icons/line/LineEllipsisVIcon'
import LineSpinnerIcon from 'LuxKit/Icons/line/LineSpinnerIcon'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import type { TripListRow } from './tripListTypes'
import TripListRowPrice from './TripListRowPrice'
import { TRIP_LIST_GRID_COLUMNS } from './tripListLayout'
import TripListTypeIcon from './TripListTypeIcon'
import TripListCheckboxButton from './TripListCheckboxButton'
import TripListRowNote from './TripListRowNote'

const Cell = styled.div`
  align-self: stretch;
  display: flex;
  align-items: center;
  min-width: 0;
  padding: ${rem(9)} ${rem(8)};

  &:first-child {
    justify-content: center;
  }

  &:first-child,
  &:nth-child(2) {
    align-items: flex-start;
    padding-top: ${rem(16)};
    border-right: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  }

  &:nth-child(3) {
    align-items: flex-start;
    padding-top: ${rem(16)};
  }

  &:nth-child(8) {
    justify-content: flex-end;
    padding-right: ${rem(4)};
  }
`

const Row = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: ${TRIP_LIST_GRID_COLUMNS};
  min-height: ${rem(46)};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  cursor: pointer;
  transition: box-shadow 120ms ease, background 120ms ease;

  & + & {
    ${Cell}:nth-child(n + 4) {
      border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
    }
  }

  &:hover {
    z-index: 1;
    background: ${props => props.theme.palette.neutral.surface.lightBackground};
    box-shadow: 0 ${rem(4)} ${rem(12)} rgba(0, 0, 0, 0.12);

    ${Cell}:nth-child(n + 4) {
      border-top-color: transparent;
    }
  }

  &.is-selected,
  &.is-selected:hover {
    background: ${props => props.theme.palette.highlight.primary.lightBackground};
  }

  &.is-first-row {
    border-top-left-radius: ${props => props.theme.borderRadius.L};
    border-top-right-radius: ${props => props.theme.borderRadius.L};
  }

  &.is-last-row {
    border-bottom-right-radius: ${props => props.theme.borderRadius.L};
    border-bottom-left-radius: ${props => props.theme.borderRadius.L};
  }
`

const RowCheckboxSlot = styled.div`
  opacity: 0;
  transition: opacity 120ms ease;
  cursor: pointer;

  input {
    cursor: pointer;
  }

  ${Row}:hover &,
  &.is-selected {
    opacity: 1;
  }
`

const CompactBookButton = styled(TextButton)`
  min-width: ${rem(56)};
  padding-right: ${rem(12)};
  padding-left: ${rem(12)};
  border-radius: ${props => props.theme.borderRadius.round};
`

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${rem(2)};
  min-width: 0;
  width: 100%;
`

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  min-height: ${rem(24)};
  padding: 0 ${rem(8)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${rem(2)};
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
  color: ${props => props.theme.palette.neutral.surface.lightContrast};

  &.is-positive {
    background: ${props => props.theme.palette.messaging.success.lightBackground};
    color: ${props => props.theme.palette.messaging.success.normalForeground};
  }
`

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

const SpinnerIcon = styled(LineSpinnerIcon)`
  animation: ${spin} 800ms linear infinite;
`

interface Props {
  row: TripListRow
  selected: boolean
  showGroupMetadata: boolean
  firstRow: boolean
  lastRow: boolean
  luxPlusEnabled: boolean
  onSelectRow: (rowId: string, checked: boolean) => void
  onRowClick: (row: TripListRow) => void
  onBookRow: (row: TripListRow) => void
  onViewBooking: (row: TripListRow) => void
  onAskAi: (row: TripListRow) => void
  onDelete: (row: TripListRow) => void
  onUpdateNote: (row: TripListRow, notes: string | undefined) => void
  isBookingPending?: boolean
  isNoteUpdatePending?: boolean
  notesDisabled?: boolean
  actionsDisabled?: boolean
}

function TripListRowItem({
  row,
  selected,
  showGroupMetadata,
  firstRow,
  lastRow,
  luxPlusEnabled,
  onSelectRow,
  onRowClick,
  onBookRow,
  onViewBooking,
  onAskAi,
  onDelete,
  onUpdateNote,
  isBookingPending = false,
  isNoteUpdatePending = false,
  notesDisabled = false,
  actionsDisabled = false,
}: Props) {
  const intl = useIntl()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuTriggerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (actionsDisabled) {
      setMenuOpen(false)
    }
  }, [actionsDisabled])

  const handleRowClick = useCallback(() => {
    onRowClick(row)
  }, [onRowClick, row])

  const handleControlClick = useCallback((event: MouseEvent) => {
    event.stopPropagation()
  }, [])

  const handleCheckboxChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    onSelectRow(row.id, event.currentTarget.checked)
  }, [onSelectRow, row.id])

  const toggleMenu = useCallback((event: MouseEvent) => {
    event.stopPropagation()
    if (actionsDisabled) return
    setMenuOpen(prev => !prev)
  }, [actionsDisabled])

  const closeMenu = useCallback(() => {
    setMenuOpen(false)
  }, [])

  const handleAskAi = useCallback(() => {
    if (actionsDisabled) return
    closeMenu()
    onAskAi(row)
  }, [actionsDisabled, closeMenu, onAskAi, row])

  const handleDelete = useCallback(() => {
    if (actionsDisabled) return
    closeMenu()
    onDelete(row)
  }, [actionsDisabled, closeMenu, onDelete, row])

  const handleNoteChange = useCallback((notes: string | undefined) => {
    if (notesDisabled || isNoteUpdatePending) return
    onUpdateNote(row, notes)
  }, [isNoteUpdatePending, notesDisabled, onUpdateNote, row])

  const handleCtaClick = useCallback((event: MouseEvent) => {
    event.stopPropagation()
    if (actionsDisabled || isBookingPending) return
    onBookRow(row)
  }, [actionsDisabled, isBookingPending, onBookRow, row])

  const handleViewBookingClick = useCallback((event: MouseEvent) => {
    event.stopPropagation()
    if (actionsDisabled) return
    onViewBooking(row)
  }, [actionsDisabled, onViewBooking, row])

  const handleViewRowClick = useCallback((event: MouseEvent) => {
    event.stopPropagation()
    if (actionsDisabled) return
    onRowClick(row)
  }, [actionsDisabled, onRowClick, row])

  const bookLabel = intl.formatMessage({
    id: 'tumE0yl0JumvbIhBIWAD',
    defaultMessage: 'Book',
    description: 'CTA button label for bookable rows in the trip planner list view',
  })

  const viewBookingLabel = intl.formatMessage({
    id: 'XJ481es7biCMHYe_wDuK',
    defaultMessage: 'Manage',
    description: 'CTA button label for managing booked rows in the trip planner list view',
  })

  const viewLabel = intl.formatMessage({
    id: '9V1THFwaUjdBLk4x3kKO',
    defaultMessage: 'View',
    description: 'CTA button label for viewing rows in the trip planner list view',
  })

  return (
    <Row
      onClick={handleRowClick}
      className={clsx(
        selected && 'is-selected',
        firstRow && 'is-first-row',
        lastRow && 'is-last-row',
      )}
    >
      <Cell onClick={handleControlClick}>
        <RowCheckboxSlot className={selected ? 'is-selected' : undefined}>
          <TripListCheckboxButton
            size="small"
            className="is-table-checkbox"
            checked={selected}
            onChange={handleCheckboxChange}
            aria-label={intl.formatMessage(
              {
                id: 'qevuYZDilBy9necmVigz',
                defaultMessage: 'Select {title}',
                description: 'Accessible label for selecting a row in the trip planner list view',
              },
              { title: row.title },
            )}
          />
        </RowCheckboxSlot>
      </Cell>
      <Cell>
        {showGroupMetadata && <BodyText variant="medium" colour="normal-contrast" lineClamp={1}>{row.dayLabel}</BodyText>}
      </Cell>
      <Cell>
        {showGroupMetadata && (
          <BodyText variant="medium" colour="normal-contrast" lineClamp={1}>
            {row.location.label}
          </BodyText>
        )}
      </Cell>
      <Cell>
        <Group direction="horizontal" gap={12} verticalAlign="center" fullWidth>
          <TripListTypeIcon type={row.rowType} row={row} />
          <Group direction="vertical" gap={2}>
            <BodyText variant="medium" weight="bold" lineClamp={1}>{row.title}</BodyText>
            {row.subtitle && <Caption variant="large" colour="light-foreground-alt" lineClamp={1}>{row.subtitle}</Caption>}
          </Group>
        </Group>
      </Cell>
      <Cell>
        <TripListRowNote
          note={row.note}
          title={row.title}
          disabled={notesDisabled}
          saving={isNoteUpdatePending}
          onChange={handleNoteChange}
        />
      </Cell>
      <Cell>
        <StatusPill className={row.status.tone === 'positive' ? 'is-positive' : undefined}>
          <Caption variant="medium" weight="bold" lineClamp={1}>{row.status.label}</Caption>
        </StatusPill>
      </Cell>
      <Cell>
        <TripListRowPrice price={row.price} luxPlusEnabled={luxPlusEnabled} />
      </Cell>
      <Cell onClick={handleControlClick}>
        <ActionGroup>
          {row.isBooked && (
            <CompactBookButton size="small" kind="secondary" onClick={handleViewBookingClick} disabled={actionsDisabled}>
              {viewBookingLabel}
            </CompactBookButton>
          )}
          {!row.isBooked && row.isBookable && (
            <CompactBookButton
              size="small"
              kind="primary"
              onClick={handleCtaClick}
              disabled={actionsDisabled || isBookingPending}
              aria-busy={isBookingPending}
              startIcon={isBookingPending ? <SpinnerIcon size="XS" /> : false}
            >
              {bookLabel}
            </CompactBookButton>
          )}
          {!row.isBooked && !row.isBookable && (
            <CompactBookButton size="small" kind="secondary" onClick={handleViewRowClick} disabled={actionsDisabled}>
              {viewLabel}
            </CompactBookButton>
          )}
          <IconButton
            size="small"
            kind="tertiary"
            onClick={toggleMenu}
            ref={menuTriggerRef}
            disabled={actionsDisabled}
          >
            <LineEllipsisVIcon />
          </IconButton>
          <DropdownList
            size="S"
            open={menuOpen}
            onClose={closeMenu}
            triggerRef={menuTriggerRef}
            anchorRef={menuTriggerRef}
            placement="bottom-end"
            portaled
          >
            <ClickableListItem
              title={intl.formatMessage({
                id: 'G352qlaFJTQXJzpg0Sg2',
                defaultMessage: 'Ask AI',
                description: 'Dropdown menu item for asking AI about a trip planner list row',
              })}
              size="medium"
              startIcon={<AISparkleIcon size="S" />}
              onClick={handleAskAi}
            />
            <ClickableListItem
              title={intl.formatMessage({
                id: 'NQx7k4Q98rLpfRiwQBQK',
                defaultMessage: 'Delete',
                description: 'Dropdown menu item for deleting a trip planner list row',
              })}
              size="medium"
              startIcon={<LineTrashAltIcon size="S" />}
              onClick={handleDelete}
            />
          </DropdownList>
        </ActionGroup>
      </Cell>
    </Row>
  )
}

export default TripListRowItem
